// Pure helpers for the Forms editor's Sessions section. They live outside
// FormsAdmin.tsx so that file only exports components (react-refresh), and so
// the rules that matter — how a key is slugged, which Affinity fields may be
// picked — are testable without mounting the editor.

import type { AffinityListField, FormSessionIn } from '../../types/outreach';

/**
 * A session row while it's being edited. `keyTouched` is local-only: until the
 * admin edits the key by hand it keeps tracking the label, after which it's
 * frozen — the key is what historical answers are filed under, so silently
 * re-slugging it on a label tweak would orphan them.
 */
export type SessionDraft = FormSessionIn & { keyTouched?: boolean };

/** label -> the `^[a-z0-9][a-z0-9_]{0,31}$` key the server accepts. */
export function slugifySessionKey(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^[^a-z0-9]+/, '')
    .slice(0, 32)
    .replace(/_+$/, '');
}

export function emptySession(): SessionDraft {
  return {
    session_key: '',
    label: '',
    when_text: '',
    where_text: '',
    is_primary: false,
    affinity_field_id: null,
    // Only meaningful once a field is picked; with no field the server stores
    // them and nothing reads them.
    yes_option_label: 'Yes',
    no_option_label: 'No',
    dropdown_options: {},
  };
}

/**
 * The empty sentinel for the Yes/No option selects.
 *
 * ⚠ NOT a default of `dropdown_options[0]` / `[1]`. Affinity returns options in
 * ITS order, and on the real field that order is
 * `No Response, RSVP'd Attending, RSVP'd Not Attending` — so defaulting to the
 * first two would map **Yes → "No Response"** for an admin who picks the field
 * and saves without opening the selects. That is a silent, plausible-looking
 * miswrite of every Yes on the form. An unset sentinel makes the admin choose,
 * and `validateSessions` refuses the save until they have.
 */
export const OPTION_UNSET = '';

/**
 * THE one representation of "this row writes back nowhere".
 *
 * Both paths that drop a write-back target — picking the blank row in the field
 * picker, and changing the linked list — spread this, so they cannot drift
 * apart. The labels go back to the `'Yes'` / `'No'` DEFAULTS rather than
 * `OPTION_UNSET`: with no field id `validateSessions` skips the row, so an
 * empty label would sail past the client and land on
 * `FormSessionIn.yes_option_label`'s `min_length=1` as a raw 422.
 *
 * The labels are inert while `affinity_field_id` is null — the server stores
 * them and nothing reads them — so the defaults are honest, not a guess.
 */
export const CLEARED_WRITEBACK = {
  affinity_field_id: null,
  dropdown_options: {} as Record<string, number>,
  yes_option_label: 'Yes',
  no_option_label: 'No',
};

/**
 * The Affinity fields a session may write back to: LIST-SCOPED dropdowns on
 * this form's single list, minus the ones the engine owns.
 *
 * Affinity V1 `/fields?list_id=N` returns the global person/org fields in the
 * same response (they carry no `list_id`), and one of those would be rejected
 * on a list-entry write — so they're filtered out here rather than offered to
 * the admin and failed at submission time.
 *
 * `reservedFieldIds` are the list config's own Status / dietary / comments /
 * scope / suppression fields. Status especially must never be offered:
 * `apply_rsvp_to_recipient` writes Status FIRST and the session answers LAST,
 * so a session pointed there would overwrite the status the same submission
 * just wrote. The server rejects these too — this keeps them out of the menu so
 * the admin cannot pick one in the first place.
 */
export function listScopedDropdownFields(
  fields: AffinityListField[] | undefined,
  listId: number | null,
  reservedFieldIds: Iterable<number> = [],
): AffinityListField[] {
  if (!fields || listId == null) return [];
  const reserved = new Set(reservedFieldIds);
  return fields.filter(
    (f) => f.list_id === listId
      && f.dropdown_options.length > 0
      && !reserved.has(f.id),
  );
}

/**
 * The list config's engine-owned field ids. Mirrors
 * `_RESERVED_LIST_FIELD_COLUMNS` in `routers/outreach.py`; the server is the
 * authority and 400s on these either way.
 */
export function reservedFieldIdsForList(
  list: {
    status_field_id?: number | null;
    dietary_field_id?: number | null;
    comments_field_id?: number | null;
    scope_field_id?: number | null;
    suppression_field_id?: number | null;
    suppression_reason_field_id?: number | null;
  } | undefined,
): number[] {
  return [
    list?.status_field_id,
    list?.dietary_field_id,
    list?.comments_field_id,
    list?.scope_field_id,
    list?.suppression_field_id,
    list?.suppression_reason_field_id,
  ].filter((v): v is number => typeof v === 'number');
}
